import { useState, useRef, useEffect, KeyboardEvent, Fragment } from 'react';

// material-ui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import InputAdornment from '@mui/material/InputAdornment';
import MuiLink from '@mui/material/Link';
import OutlinedInput from '@mui/material/OutlinedInput';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// router
import { Link as RouterLink } from 'react-router-dom';

// project imports
import IconButton from 'components/@extended/IconButton';
import { askHoa } from 'api/askHoa';
import { useWorkspace } from 'contexts/WorkspaceContext';

// assets
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import RobotOutlined from '@ant-design/icons/RobotOutlined';
import SendOutlined from '@ant-design/icons/SendOutlined';

// ==============================|| AI CHAT - TYPES ||============================== //

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface AiChatOpenEventDetail {
  open?: boolean;
  prompt?: string;
  sessionId?: string;
  autoSend?: boolean;
}

// ==============================|| AI CHAT - INLINE MARKDOWN RENDERER ||============================== //

/**
 * Parses a line of text for **bold** and [text](url) markdown and returns React nodes.
 * Internal paths (starting with /) are rendered as router Links; external URLs as <a>.
 */
function renderInline(text: string, isUser: boolean): React.ReactNode[] {
  const TOKEN_RE = /(\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\))/g;
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = TOKEN_RE.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push(text.slice(last, match.index));
    }

    if (match[0].startsWith('**')) {
      // Bold
      nodes.push(<strong key={match.index}>{match[2]}</strong>);
    } else {
      // Link
      const label = match[3];
      const href = match[4];
      const isInternal = href.startsWith('/');
      nodes.push(
        isInternal ? (
          <MuiLink
            key={match.index}
            component={RouterLink}
            to={href}
            sx={{ color: isUser ? 'primary.contrastText' : 'primary.main', fontWeight: 500 }}
          >
            {label}
          </MuiLink>
        ) : (
          <MuiLink
            key={match.index}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: isUser ? 'primary.contrastText' : 'primary.main', fontWeight: 500 }}
          >
            {label}
          </MuiLink>
        )
      );
    }

    last = match.index + match[0].length;
  }

  if (last < text.length) {
    nodes.push(text.slice(last));
  }

  return nodes;
}

function renderMarkdown(content: string, isUser: boolean): React.ReactNode {
  return content.split('\n').map((line, i, arr) => (
    <Fragment key={i}>
      {renderInline(line, isUser)}
      {i < arr.length - 1 && <br />}
    </Fragment>
  ));
}

// ==============================|| AI CHAT - MESSAGE BUBBLE ||============================== //

function MessageBubble({ message }: { message: Message }) {
  const theme = useTheme();
  const isUser = message.role === 'user';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 1.5
      }}
    >
      <Box
        sx={{
          maxWidth: '80%',
          px: 1.75,
          py: 1.25,
          borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          bgcolor: isUser ? 'primary.main' : theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
          color: isUser ? 'primary.contrastText' : 'text.primary'
        }}
      >
        <Typography variant="body2" sx={{ lineHeight: 1.6 }} component="div">
          {renderMarkdown(message.content, isUser)}
        </Typography>
      </Box>
    </Box>
  );
}

// ==============================|| AI CHAT - THINKING BUBBLE ||============================== //

function ThinkingBubble() {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderRadius: '16px 16px 16px 4px',
          bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center">
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'text.secondary',
                animation: 'bounce 1.2s infinite ease-in-out',
                animationDelay: `${i * 0.2}s`,
                '@keyframes bounce': {
                  '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
                  '40%': { transform: 'scale(1)', opacity: 1 }
                }
              }}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

// ==============================|| AI CHAT - MAIN COMPONENT ||============================== //

export default function AiChat() {
  const theme = useTheme();
  const { activeCommunityId } = useWorkspace();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const pendingSendRef = useRef(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  // Auto-scroll to latest message
  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading, open]);

  useEffect(() => {
    const handleExternalOpen = (event: Event) => {
      const customEvent = event as CustomEvent<AiChatOpenEventDetail>;
      const detail = customEvent.detail || {};

      if (detail.open !== false) {
        setOpen(true);
      }

      if (typeof detail.sessionId === 'string') {
        setSessionId(detail.sessionId);
      }

      if (typeof detail.prompt === 'string') {
        setInput(detail.prompt);
        pendingSendRef.current = Boolean(detail.autoSend);
      }
    };

    window.addEventListener('ai-chat-open', handleExternalOpen as EventListener);
    return () => {
      window.removeEventListener('ai-chat-open', handleExternalOpen as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!open || loading || !pendingSendRef.current) {
      return;
    }

    const hasInput = input.trim().length > 0;
    if (!hasInput) {
      return;
    }

    pendingSendRef.current = false;
    handleSend();
  }, [open, loading, input]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading || !activeCommunityId) return;

    const userMessage: Message = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await askHoa({
        question: text,
        communityId: activeCommunityId,
        sessionId
      });

      setSessionId(response.sessionId);
      setMessages((prev) => [...prev, { role: 'assistant', content: response.answer }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: "Sorry, I wasn't able to get a response. Please try again." }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setSessionId(undefined);
    setInput('');
  };

  return (
    <>
      {/* Header icon button */}
      <Box sx={{ flexShrink: 0 }}>
        <Tooltip title="AI Assistant">
          <IconButton
            color="secondary"
            variant="light"
            sx={(t) => ({
              color: 'text.primary',
              bgcolor: open ? 'grey.100' : 'transparent',
              ...t.applyStyles('dark', { bgcolor: open ? 'background.default' : 'transparent' })
            })}
            aria-label="open AI assistant"
            onClick={handleToggle}
          >
            <RobotOutlined />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Chat Drawer */}
      <Drawer
        sx={{ zIndex: 2001 }}
        anchor="right"
        open={open}
        onClose={handleToggle}
        slotProps={{ paper: { sx: { width: { xs: 340, sm: 420 }, display: 'flex', flexDirection: 'column' } } }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <RobotOutlined style={{ fontSize: 20, color: theme.palette.primary.main }} />
            <Typography variant="h5">AI Assistant</Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {messages.length > 0 && (
              <Tooltip title="New chat">
                <IconButton size="small" color="secondary" variant="light" onClick={handleNewChat} aria-label="new chat">
                  <Typography variant="caption" sx={{ px: 0.5 }}>
                    New chat
                  </Typography>
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" color="secondary" variant="light" onClick={handleToggle} aria-label="close">
              <CloseOutlined />
            </IconButton>
          </Stack>
        </Box>

        <Divider />

        {/* No community selected warning */}
        {!activeCommunityId && (
          <Box sx={{ px: 2, pt: 2 }}>
            <Alert severity="warning" variant="outlined">
              Select a community to start asking questions.
            </Alert>
          </Box>
        )}

        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            py: 2,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {messages.length === 0 && activeCommunityId && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                color: 'text.secondary',
                textAlign: 'center',
                px: 3
              }}
            >
              <RobotOutlined style={{ fontSize: 40, opacity: 0.3 }} />
              <Typography variant="body2">
                Ask me anything about your community — rules, guidelines, announcements, and your maintenance requests or violations.
              </Typography>
            </Box>
          )}

          {messages.map((msg, i) => (
            <MessageBubble key={i} message={msg} />
          ))}

          {loading && <ThinkingBubble />}

          <div ref={messagesEndRef} />
        </Box>

        <Divider />

        {/* Input */}
        <Box sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
          <OutlinedInput
            fullWidth
            multiline
            maxRows={4}
            placeholder={activeCommunityId ? 'Ask a question…' : 'Select a community first'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={!activeCommunityId || loading}
            size="small"
            sx={{ pr: 0.5 }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={handleSend}
                  disabled={!input.trim() || !activeCommunityId || loading}
                  aria-label="send message"
                  sx={{ mr: 0.25 }}
                >
                  {loading ? <CircularProgress size={14} color="inherit" /> : <SendOutlined />}
                </IconButton>
              </InputAdornment>
            }
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block', textAlign: 'center' }}>
            Answers are based on your community documents, requests, and violations.
          </Typography>
        </Box>
      </Drawer>
    </>
  );
}
